import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import Barometer from './components/Barometer';
import DataTable from './components/DataTable';
import Explanations from './components/Explanations';
import './styles.css';

function App() {
  const [quarterlyData, setQuarterlyData] = useState({
    boligsalg: {},
    boligpris: {},
  });
  const [monthlyData, setMonthlyData] = useState({
    inflation: {},
    unemployment: {},
    rente: {},
  });
  const [quarters, setQuarters] = useState([]);
  const [forSaleData, setForSaleData] = useState({});
  const [afslagData, setAfslagData] = useState({});
  const [liggetidData, setLiggetidData] = useState({});
  const [bnpData, setBnpData] = useState({}); // Added state for BNP data

  const dataUrl = 'https://api.statbank.dk/v1/data';
  const finansDataUrl = 'https://api.statbank.dk/v1/S20/data';

  // Utility functions

  /**
   * Maps indices to labels for a given dimension.
   *
   * @param {Object} dimension - The dimension object from the dataset.
   * @returns {Object} - A mapping from index to label.
   */
  const mapIndicesToLabels = (dimension) => {
    const indices = dimension.category.index;
    const labels = {};
    for (const [key, index] of Object.entries(indices)) {
      labels[index] = key; // Map index to label
    }
    return labels;
  };

  /**
   * Calculates strides for multi-dimensional array access.
   *
   * @param {Array} sizes - Array of dimension sizes.
   * @returns {Array} - Strides for each dimension.
   */
  const calculateStrides = (sizes) => {
    const strides = [];
    strides[sizes.length - 1] = 1;
    for (let i = sizes.length - 2; i >= 0; i--) {
      strides[i] = strides[i + 1] * sizes[i + 1];
    }
    return strides;
  };

  /**
   * Parses a quarter string (e.g., "2023K2") into an object with year and quarter number.
   *
   * @param {string} quarterStr - The quarter string.
   * @returns {Object} - { year: number, quarter: number }
   */
  const parseQuarterString = (quarterStr) => {
    const year = parseInt(quarterStr.slice(0, 4));
    const quarter = parseInt(quarterStr.slice(-1));
    return { year, quarter };
  };

  /**
   * Calculates the year-over-year percentage change.
   *
   * @param {Object} data - The data object with values per period.
   * @param {string} period - The current period (e.g., quarter or month).
   * @param {string} periodType - 'quarter' or 'month'
   * @returns {number|null} - The percentage change or null if not calculable.
   */
  const calculateYearOverYearPercentageChange = (data, period, periodType) => {
    if (!data || !data[period]) return null;
    
    const currentValue = data[period];
    if (currentValue === null) return null;

    let prevYearPeriod;
    if (periodType === 'quarter') {
      const { year, quarter } = parseQuarterString(period);
      prevYearPeriod = `${year - 1}K${quarter}`;
    } else if (periodType === 'month') {
      const year = parseInt(period.slice(0, 4));
      const month = period.slice(5);
      prevYearPeriod = `${year - 1}M${month}`;
    }

    const previousValue = data[prevYearPeriod];
    if (previousValue === null || previousValue === 0) return null;

    return ((currentValue - previousValue) / previousValue) * 100;
  };

  // Function to calculate quarter-over-quarter percentage change for BNP
  const calculateQuarterOverQuarterChange = (data, quarter) => {
    const currentValue = data?.[quarter];

    const { year, quarter: quarterNumber } = parseQuarterString(quarter);

    let prevQuarterNumber = quarterNumber - 1;
    let prevYear = year;
    if (prevQuarterNumber === 0) {
      prevQuarterNumber = 4;
      prevYear = year - 1;
    }
    const prevQuarter = `${prevYear}K${prevQuarterNumber}`;

    const previousValue = data?.[prevQuarter];

    if (
      currentValue !== undefined &&
      previousValue !== undefined &&
      previousValue !== 0
    ) {
      const change = ((currentValue - previousValue) / previousValue) * 100;
      return change;
    } else {
      return null; // Return null if data is missing or previous value is zero
    }
  };

  // Function to get the last N quarters dynamically, excluding future quarters
  const getLastNQuarters = (n) => {
    const quarters = [];
    const date = new Date();
    let year = date.getFullYear();
    let quarter = Math.floor((date.getMonth() + 1) / 3); // 1-4

    // Go back TWO quarters to ensure data availability
    quarter -= 1;
    if (quarter <= 0) {
      quarter = 4 + quarter; // quarter is negative, so add it
      year--;
    }

    // Generate quarters
    for (let i = 0; i < n + 4; i++) {
      quarters.unshift(`${year}K${quarter}`);
      quarter--;
      if (quarter === 0) {
        quarter = 4;
        year--;
      }
    }

    return quarters;
  };

  // Function to get all months needed for the given quarters, excluding future months
  const getAllNeededMonths = (quarters) => {
    const monthsSet = new Set();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-11

    quarters.forEach((quarter) => {
      const { year, quarter: quarterNumber } = parseQuarterString(quarter);

      const monthNumbers = [
        (quarterNumber - 1) * 3 + 1,
        (quarterNumber - 1) * 3 + 2,
        (quarterNumber - 1) * 3 + 3,
      ];

      monthNumbers.forEach((monthNum) => {
        // Only add months that aren't in the future
        if (year < currentYear || (year === currentYear && monthNum <= currentMonth + 1)) {
          const monthStr = String(monthNum).padStart(2, '0');
          monthsSet.add(`${year}M${monthStr}`);
          monthsSet.add(`${year - 1}M${monthStr}`); // previous year months for YoY calculations
        }
      });
    });

    return Array.from(monthsSet).sort();
  };

  // Fetch functions

  const fetchQuarterlyData = async (quarters) => {
    const availableQuarters = quarters.filter((q) => {
      const { year, quarter: quarterNumber } = parseQuarterString(q);
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentQuarter = Math.floor(currentDate.getMonth() / 3) + 1;

      return (
        year < currentYear ||
        (year === currentYear && quarterNumber < currentQuarter)
      );
    });

    console.log('SHIT SHIT SHIT', availableQuarters)

    if (availableQuarters.length === 0) return;

    async function doFetch() {
      const requestData = {
        table: 'EJEN77',
        format: 'JSONSTAT',
        variables: [
          { code: 'OMRÅDE', values: ['000'] },
          { code: 'EJENDOMSKATE', values: ['0111', '0801', '2102'] },
          { code: 'BNØGLE', values: ['2', '3'] },
          { code: 'OVERDRAG', values: ['1'] },
          { code: 'Tid', values: availableQuarters },
        ],
      };

      return fetch(dataUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      }).then((response) => response.json())
    }

    while (availableQuarters.length > 0) {
      const data = await doFetch();
      if (data.errorTypeCode) {
        availableQuarters.pop();
        continue;
      } else if (data && data.dataset) {
        console.log('Quarterly Data Response:', data);
        processQuarterlyData(data);
        break;
      } else {
        console.error('Invalid data format:', data);
      }
    }
  };

  const fetchMonthlyData = (months) => {
    const availableMonths = filterAvailableMonths(months);
    if (availableMonths.length === 0) return;

    const requestData = {
      table: 'PRIS111',
      format: 'JSONSTAT',
      variables: [
        { code: 'VAREGR', values: ['000000'] },
        { code: 'ENHED', values: ['100'] },
        { code: 'Tid', values: availableMonths },
      ],
    };

    fetchData(requestData, 'inflation', true);
  };

  const fetchUnemploymentData = (months) => {
    // Filter out future months
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    const availableMonths = months.filter(m => {
      const year = parseInt(m.slice(0, 4));
      const month = parseInt(m.slice(-2));
      return year < currentYear || (year === currentYear && month <= currentMonth);
    });

    if (availableMonths.length === 0) return;

    const requestData = {
      table: 'AUP01',
      format: 'JSONSTAT',
      variables: [
        { code: 'OMRÅDE', values: ['000'] },
        { code: 'ALDER', values: ['TOT'] },
        { code: 'Tid', values: availableMonths },
        { code: 'KØN', values: ['TOT'] },
      ],
    };

    fetchData(requestData, 'unemployment', true);
  };

  const fetchRenteData = (months) => {
    const requestData = {
      table: 'DNRNURI',
      format: 'JSONSTAT',
      variables: [
        {
          code: 'DATA',
          values: ['AL51EFFR'],
        },
        {
          code: 'INDSEK',
          values: ['1430'],
        },
        {
          code: 'VALUTA',
          values: ['Z01'],
        },
        {
          code: 'RENTFIX',
          values: ['S10A'],
        },
        {
          code: 'LAANSTR',
          values: ['ALLE'],
        },
        {
          code: 'Tid',
          values: months,
        },
      ],
    };

    fetchData(requestData, 'rente', true);
  };

  const fetchForSaleData = async (quarters) => {
    const availableQuarters = [...quarters];
    if (availableQuarters.length === 0) return;

    async function doFetch() {
      const requestData = {
        table: 'BM020',
        format: 'JSONSTAT',
        variables: [
          { code: 'OMR20', values: ['00'] },
          { code: 'EJKAT20', values: ['1', '2', '3'] },
          { code: 'BEV20', values: ['ULTIMO'] },
          { code: 'Tid', values: availableQuarters },
        ],
      };
  
      return await fetch(finansDataUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      })
        .then((response) => response.json())
    }

    while (availableQuarters.length > 0) {
      const data = await doFetch();
      if (data.errorTypeCode) {
        availableQuarters.pop()
        continue;
      } else if (data && data.dataset) {
        processForSaleData(data);
        break;
      } else {
        console.error('Invalid data format:', data);
      }
    }
  };

  // Function to fetch Liggetid data
  const fetchLiggetidData = async (quarters) => {
    // Filter out future quarters
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentQuarter = Math.floor((currentDate.getMonth() + 3) / 3);

    const availableQuarters = quarters.filter(q => {
      const { year, quarter } = parseQuarterString(q);
      return year < currentYear || (year === currentYear && quarter < currentQuarter);
    });

    if (availableQuarters.length === 0) return;

    async function doFetch() {
      const requestData = {
        table: 'BM030',
        format: 'JSONSTAT',
        variables: [
          {
            code: 'OMR20',
            values: ['00'],
          },
          {
            code: 'EJKAT20',
            values: ['1', '2', '3'],
          },
          {
            code: 'Tid',
            values: availableQuarters,
          },
        ],
      };
  
      return await fetch(finansDataUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      }).then((response) => response.json())
    }

    while (availableQuarters.length > 0) {
      const data = await doFetch();

      console.log('Liggetid Data Response:', data);
      if (data.errorTypeCode) {
        availableQuarters.pop()
        continue;
      } else if (data && data.dataset) {
        processLiggetidData(data);
        break;
      } else {
        console.error('Invalid data format:', data);
      }
    }
  };

  const fetchAfslagData = async (quarters) => {
    const availableQuarters = [...quarters];

    async function doFetch() {
      const requestData = {
        table: 'BM010',
        format: 'JSONSTAT',
        variables: [
          {
            code: 'OMR20',
            values: ['00'],
          },
          {
            code: 'EJKAT20',
            values: ['1', '2', '3'],
          },
          {
            code: 'PRIS20',
            values: ['UDBUD', 'REAL'],
          },
          {
            code: 'Tid',
            values: availableQuarters,
          },
        ],
      };

      return await fetch(finansDataUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      })
        .then((response) => response.json())
    }

    while (quarters.length > 0) {
      const data = await doFetch();
        console.log('Afslag Data Response:', data);
      if (data.errorTypeCode) {
        availableQuarters.pop()
        continue;
      } else if (data && data.dataset) {
        processAfslagData(data);
        break;
      } else {
        console.error('Invalid data format:', data);
      }
    }
  };

  const fetchBNPData = (quarters) => {
    // Filter out future quarters
    const availableQuarters = quarters.filter((q) => {
      const { year, quarter: quarterNumber } = parseQuarterString(q);
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentQuarter = Math.floor((currentDate.getMonth() + 3) / 3);
      return (
        year < currentYear ||
        (year === currentYear && quarterNumber <= currentQuarter)
      );
    });

    const requestData = {
      table: 'NKN1',
      format: 'JSONSTAT',
      variables: [
        {
          code: 'TRANSAKT',
          values: ['B1GQK'],
        },
        {
          code: 'PRISENHED',
          values: ['L_VB'],
        },
        {
          code: 'SÆSON',
          values: ['Y'],
        },
        {
          code: 'Tid',
          values: availableQuarters,
        },
      ],
    };

    fetch(dataUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('BNP Data Response:', data);
        if (data.errorTypeCode) {
        } else if (data && data.dataset) {
          processBNPData(data);
        } else {
          console.error('Invalid data format:', data);
        }
      })
      .catch((error) => {
        console.error('Error fetching BNP data:', error);
      });
  };

  const fetchData = (requestData, type = 'quarterly', isMonthly = false) => {
    fetch(dataUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('API Response:', data);
        if (data.errorTypeCode) {
        } else if (data && data.dataset) {
          if (isMonthly) {
            processMonthlyData(data, type);
          } else {
            processQuarterlyData(data);
          }
        } else {
          console.error('Invalid data format:', data);
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  };

  // Data processing functions

  const processQuarterlyData = (data) => {
    if (!data || !data.dataset) {
      console.warn('No dataset available in quarterly data');
      return;
    }

    console.log('Processing quarterly data:', data);

    const dimensions = data.dataset.dimension;
    const values = data.dataset.value;

    // Use the utility functions to map indices to labels
    const timeLabels = mapIndicesToLabels(dimensions['Tid']);
    const propertyLabels = mapIndicesToLabels(dimensions['EJENDOMSKATE']);
    const bnogleLabels = mapIndicesToLabels(dimensions['BNØGLE']);

    console.log('Mapped labels:', { timeLabels, propertyLabels, bnogleLabels });

    // Get dimension sizes
    const dimensionIds = dimensions.id;
    const sizes = dimensionIds.map(
      (id) => Object.keys(dimensions[id].category.index).length
    );

    const strides = calculateStrides(sizes);
    const dataByType = { boligsalg: {}, boligpris: {} };

    for (let i = 0; i < values.length; i++) {
      let remainder = i;
      const idx = [];
      for (let d = 0; d < sizes.length; d++) {
        idx[d] = Math.floor(remainder / strides[d]) % sizes[d];
        remainder = remainder % strides[d];
      }

      const timeIdx = idx[dimensionIds.indexOf('Tid')];
      const timeLabel = timeLabels[timeIdx];

      const propertyIdx = idx[dimensionIds.indexOf('EJENDOMSKATE')];
      const propertyLabel = propertyLabels[propertyIdx];

      const bnogleIdx = idx[dimensionIds.indexOf('BNØGLE')];
      const bnogleValue = bnogleLabels[bnogleIdx];

      const value = values[i];

      if (value === null || value === undefined) {
        dataByType[type][timeLabel] = null;  // Explicitly set null for missing data
        continue;
      }

      const type = bnogleValue === '2' ? 'boligsalg' : 'boligpris';

      if (!dataByType[type][timeLabel]) dataByType[type][timeLabel] = {};
      dataByType[type][timeLabel][propertyLabel] = value;
    }

    console.log('Data by type before aggregation:', dataByType);

    // Calculate the average price or sum sales for each quarter
    const updatedQuarterlyData = { boligsalg: {}, boligpris: {} };

    for (const type of ['boligsalg', 'boligpris']) {
      for (const quarter in dataByType[type]) {
        const properties = dataByType[type][quarter];
        const valuesArray = Object.values(properties).filter((v) => v !== null);

        if (valuesArray.length > 0) {
          if (type === 'boligsalg') {
            const total = valuesArray.reduce((sum, val) => sum + val, 0);
            updatedQuarterlyData[type][quarter] = total;
          } else if (type === 'boligpris') {
            const avg = valuesArray.reduce((sum, val) => sum + val, 0) / valuesArray.length;
            updatedQuarterlyData[type][quarter] = avg;
          }
        } else {
          updatedQuarterlyData[type][quarter] = null;
        }
      }
    }

    console.log('Updated quarterly data:', updatedQuarterlyData);

    setQuarterlyData((prevData) => {
      console.log('Previous quarterly data:', prevData);
      const newData = {
        ...prevData,
        ...updatedQuarterlyData,
      };
      console.log('New quarterly data:', newData);
      return newData;
    });
  };

  const processMonthlyData = (data, type) => {
    if (!data || !data.dataset) {
      console.warn(`No dataset available for ${type} data`);
      return;
    }

    const timeLabels = mapIndicesToLabels(data.dataset.dimension['Tid']);
    const values = data.dataset.value;

    const dataMap = {};
    values.forEach((value, idx) => {
      const timeLabel = timeLabels[idx];
      dataMap[timeLabel] = value !== null ? value : null; // Preserve null values
    });

    setMonthlyData((prevData) => ({
      ...prevData,
      [type]: dataMap,
    }));
  };

  const processForSaleData = (data) => {
    if (!data || !data.dataset) {
      console.warn('No dataset available in for sale data');
      return;
    }

    console.log('Processing for sale data:', data);

    const dimensions = data.dataset.dimension;
    const values = data.dataset.value;

    // Map indices to labels
    const timeLabels = mapIndicesToLabels(dimensions['Tid']);
    console.log('Time labels:', timeLabels);

    // Get dimension sizes
    const dimensionIds = dimensions.id;
    const sizes = dimensionIds.map(
      (id) => Object.keys(dimensions[id].category.index).length
    );

    // Calculate strides
    const strides = calculateStrides(sizes);
    const dataByQuarter = {};

    for (let i = 0; i < values.length; i++) {
      let remainder = i;
      const idx = [];
      for (let d = 0; d < sizes.length; d++) {
        idx[d] = Math.floor(remainder / strides[d]) % sizes[d];
        remainder = remainder % strides[d];
      }

      const timeIdx = idx[dimensionIds.indexOf('Tid')];
      const timeLabel = timeLabels[timeIdx];
      const value = values[i];

      if (!dataByQuarter[timeLabel]) {
        dataByQuarter[timeLabel] = 0;
      }
      if (value !== null) {
        dataByQuarter[timeLabel] += value;
      }
    }

    console.log('Processed for sale data:', dataByQuarter);
    setForSaleData(dataByQuarter);
  };

  // Function to process Liggetid data
  const processLiggetidData = (data) => {
    const dimensions = data.dataset.dimension;
    const values = data.dataset.value;

    const timeLabels = mapIndicesToLabels(dimensions['Tid']);
    const ejKatLabels = mapIndicesToLabels(dimensions['EJKAT20']);

    // Get dimension sizes
    const dimensionIds = dimensions.id;
    const sizes = dimensionIds.map(
      (id) => Object.keys(dimensions[id].category.index).length
    );

    // Calculate strides
    const strides = calculateStrides(sizes);

    const dataByQuarter = {};

    for (let i = 0; i < values.length; i++) {
      let remainder = i;
      const idx = [];
      for (let d = 0; d < sizes.length; d++) {
        idx[d] = Math.floor(remainder / strides[d]) % sizes[d];
        remainder = remainder % strides[d];
      }

      const timeIdx = idx[dimensionIds.indexOf('Tid')];
      const timeLabel = timeLabels[timeIdx];

      const value = values[i];

      if (!dataByQuarter[timeLabel]) {
        dataByQuarter[timeLabel] = [];
      }
      if (value !== null) {
        dataByQuarter[timeLabel].push(value);
      }
    }

    // Calculate average Liggetid for each quarter
    const liggetidByQuarter = {};

    for (const quarter in dataByQuarter) {
      const valuesArray = dataByQuarter[quarter];
      if (valuesArray.length > 0) {
        const avg =
          valuesArray.reduce((sum, val) => sum + val, 0) / valuesArray.length;
        liggetidByQuarter[quarter] = avg;
      } else {
        liggetidByQuarter[quarter] = null;
      }
    }

    setLiggetidData(liggetidByQuarter);
  };

  // Function to process Afslag data
  const processAfslagData = (data) => {
    const dimensions = data.dataset.dimension;
    const values = data.dataset.value;

    const timeLabels = mapIndicesToLabels(dimensions['Tid']);
    const prisLabels = mapIndicesToLabels(dimensions['PRIS20']);

    // Get dimension sizes
    const dimensionIds = dimensions.id;
    const sizes = dimensionIds.map(
      (id) => Object.keys(dimensions[id].category.index).length
    );

    // Calculate strides
    const strides = calculateStrides(sizes);

    const dataByQuarter = {};

    for (let i = 0; i < values.length; i++) {
      let remainder = i;
      const idx = [];
      for (let d = 0; d < sizes.length; d++) {
        idx[d] = Math.floor(remainder / strides[d]) % sizes[d];
        remainder = remainder % strides[d];
      }

      const timeIdx = idx[dimensionIds.indexOf('Tid')];
      const timeLabel = timeLabels[timeIdx];

      const prisIdx = idx[dimensionIds.indexOf('PRIS20')];
      const prisLabel = prisLabels[prisIdx];

      const value = values[i];

      if (!dataByQuarter[timeLabel]) {
        dataByQuarter[timeLabel] = { UDBUD: 0, REAL: 0 };
      }

      if (value !== null) {
        dataByQuarter[timeLabel][prisLabel] += value;
      }
    }

    // Calculate Afslag for each quarter
    const afslagByQuarter = {};

    for (const quarter in dataByQuarter) {
      const udbud = dataByQuarter[quarter]['UDBUD'];
      const real = dataByQuarter[quarter]['REAL'];

      if (udbud !== 0 && real !== 0) {
        const afslag = ((udbud - real) / udbud) * 100;
        afslagByQuarter[quarter] = afslag;
      } else {
        afslagByQuarter[quarter] = null;
      }
    }

    setAfslagData(afslagByQuarter);
  };

  const processBNPData = (data) => {
    const dimensions = data.dataset.dimension;
    const values = data.dataset.value;

    // Map indices to labels
    const timeLabels = mapIndicesToLabels(dimensions['Tid']);

    const dimensionIds = dimensions.id;
    const sizes = dimensionIds.map(
      (id) => Object.keys(dimensions[id].category.index).length
    );

    // Calculate strides
    const strides = calculateStrides(sizes);

    const bnpDataByQuarter = {};

    for (let i = 0; i < values.length; i++) {
      let remainder = i;
      const idx = [];
      for (let d = 0; d < sizes.length; d++) {
        idx[d] = Math.floor(remainder / strides[d]) % sizes[d];
        remainder = remainder % strides[d];
      }

      const timeIdx = idx[dimensionIds.indexOf('Tid')];
      const timeLabel = timeLabels[timeIdx];

      const value = values[i];

      bnpDataByQuarter[timeLabel] = value;
    }

    setBnpData(bnpDataByQuarter);
  };

  // Generalized function to calculate YoY percentage change
  const calculateYearOverYearChange = (data, quarter) => {
    return calculateYearOverYearPercentageChange(data, quarter, 'quarter');
  };

  // Existing calculations for inflation, unemployment, and rente remain the same

  // Inflation calculation using the generalized function
  const calculateYearOverYearInflation = (quarter) => {
    const { year, quarter: quarterNumber } = parseQuarterString(quarter);
    const monthNumbers = [
      (quarterNumber - 1) * 3 + 1,
      (quarterNumber - 1) * 3 + 2,
      (quarterNumber - 1) * 3 + 3,
    ];

    const formatMonth = (y, m) => `${y}M${String(m).padStart(2, '0')}`;

    const currentYearMonths = monthNumbers.map((m) => formatMonth(year, m));
    const previousYearMonths = monthNumbers.map((m) =>
      formatMonth(year - 1, m)
    );

    const currentYearAverage =
      currentYearMonths.reduce(
        (sum, month) => sum + (monthlyData.inflation?.[month] || 0),
        0
      ) / 3;
    const previousYearAverage =
      previousYearMonths.reduce(
        (sum, month) => sum + (monthlyData.inflation?.[month] || 0),
        0
      ) / 3;

    if (previousYearAverage === 0 || previousYearAverage === undefined)
      return null; // Avoid division by zero

    return ((currentYearAverage - previousYearAverage) / previousYearAverage) * 100;
  };

  // Calculate average unemployment for a quarter
  const calculateQuarterlyAverageUnemployment = (quarter) => {
    const { year, quarter: quarterNumber } = parseQuarterString(quarter);
    const monthNumbers = [
      (quarterNumber - 1) * 3 + 1,
      (quarterNumber - 1) * 3 + 2,
      (quarterNumber - 1) * 3 + 3,
    ];

    const formatMonth = (y, m) => `${y}M${String(m).padStart(2, '0')}`;

    const months = monthNumbers.map((m) => formatMonth(year, m));

    const values = months.map((month) => monthlyData.unemployment?.[month]);

    const filteredValues = values.filter((v) => v !== null && v !== undefined);

    if (filteredValues.length === 0) return null;

    const average =
      filteredValues.reduce((sum, val) => sum + val, 0) / filteredValues.length;

    return average;
  };

  // Calculate average rente for a quarter
  const calculateQuarterlyAverageRente = (quarter) => {
    const { year, quarter: quarterNumber } = parseQuarterString(quarter);
    const monthNumbers = [
      (quarterNumber - 1) * 3 + 1,
      (quarterNumber - 1) * 3 + 2,
      (quarterNumber - 1) * 3 + 3,
    ];

    const formatMonth = (y, m) => `${y}M${String(m).padStart(2, '0')}`;

    const months = monthNumbers.map((m) => formatMonth(year, m));

    const values = months.map((month) => monthlyData.rente?.[month]);

    const filteredValues = values.filter((v) => v !== null && v !== undefined);

    if (filteredValues.length === 0) return null;

    const average =
      filteredValues.reduce((sum, val) => sum + val, 0) / filteredValues.length;

    return average;
  };

  // useEffect hook
  useEffect(() => {
    const fetchedQuarters = getLastNQuarters(10); // Now includes previous year's quarters
    // Keep track of only the quarters we want to display (last 10 quarters)
    const displayQuarters = fetchedQuarters.slice(4);
    setQuarters(displayQuarters);
    const months = getAllNeededMonths(fetchedQuarters);
    fetchQuarterlyData(fetchedQuarters);
    fetchMonthlyData(months);
    fetchUnemploymentData(months); // Fetch unemployment data
    fetchRenteData(months); // Fetch Rente data
    fetchForSaleData(fetchedQuarters); // Fetch 'for sale' data
    fetchAfslagData(fetchedQuarters); // Fetch Afslag data
    fetchLiggetidData(fetchedQuarters); // Fetch Liggetid data
    fetchBNPData(fetchedQuarters); // Fetch BNP data
  }, []);

  // Group quarters by year for header rendering
  const groupedQuarters = quarters.reduce((acc, quarter) => {
    const year = quarter.slice(0, 4);
    const q = quarter.slice(-1);
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(`${q}`);
    return acc;
  }, {});

  const categories = [
    { key: 'boligpris', displayName: 'Salgspris' },
    { key: 'boligsalg', displayName: 'Solgte boliger' },
    { key: 'afslag', displayName: 'Prisafslag' },
    { key: 'liggetid', displayName: 'Liggetid' },
    { key: 'forSale', displayName: 'Boliger til salg' },
    { key: 'bnp', displayName: 'BNP' },
  ];

  const TableRow = ({ category, quarters, data, calculateChange }) => {
    const displayName =
      category === 'boligsalg'
        ? 'Solgte boliger'
        : category === 'boligpris'
        ? 'Salgspris'
        : category === 'forSale'
        ? 'Boliger til salg'
        : category === 'afslag'
        ? 'Prisafslag'
        : category === 'liggetid'
        ? 'Liggetid'
        : category === 'bnp'
        ? 'BNP'
        : category.charAt(0).toUpperCase() + category.slice(1);

    return (
      <tr>
        <td>{displayName}</td>
        {quarters.map((quarter) => {
          let value = data?.[quarter] ?? null; // Use null if data is missing
          let cellClass = '';

          if (value === null) {
            cellClass = 'na-cell';
          } else {
            // Existing logic for setting cellClass based on value
          }

          return (
            <td key={quarter} className={cellClass}>
              {value !== null ? `${value.toFixed(1)}%` : 'N/A'}
            </td>
          );
        })}
      </tr>
    );
  };

  // Add this utility function near the top of your App component
  const filterFutureQuarters = (quarters) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentQuarter = Math.floor((currentDate.getMonth() + 3) / 3);

    return quarters.filter(q => {
      const { year, quarter } = parseQuarterString(q);
      return year < currentYear || (year === currentYear && quarter < currentQuarter);
    });
  };

  // 2. More conservative quarter filtering
  const filterAvailableQuarters = (quarters) => {
    const date = new Date();
    const currentYear = date.getFullYear();
    const currentQuarter = Math.floor((date.getMonth() + 1) / 3);
    
    // Go back two quarters to ensure data availability
    let availableQuarter = currentQuarter - 2;
    let availableYear = currentYear;
    
    if (availableQuarter <= 0) {
      availableQuarter = 4 + availableQuarter; // availableQuarter is negative, so add it
      availableYear--;
    }

    return quarters.filter(q => {
      const { year, quarter } = parseQuarterString(q);
      return year < availableYear || 
             (year === availableYear && quarter <= availableQuarter);
    });
  };

  // 3. More conservative month filtering
  const filterAvailableMonths = (months) => {
    const date = new Date();
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth() + 1; // 1-12
    
    // Go back 2 months to ensure data availability
    let availableMonth = currentMonth - 2;
    let availableYear = currentYear;
    
    if (availableMonth <= 0) {
      availableMonth = 12 + availableMonth; // availableMonth is negative, so add it
      availableYear--;
    }

    return months.filter(m => {
      const year = parseInt(m.slice(0, 4));
      const month = parseInt(m.slice(-2));
      return year < availableYear || 
             (year === availableYear && month <= availableMonth);
    });
  };

  return (
    <div className="app">
      <Navigation />
      <Routes>
        <Route path="/" element={<Navigate to="/data" />} />
        <Route 
          path="/data" 
          element={
            <DataTable 
              quarters={quarters}
              groupedQuarters={groupedQuarters}
              quarterlyData={quarterlyData}
              liggetidData={liggetidData}
              afslagData={afslagData}
              forSaleData={forSaleData}
              bnpData={bnpData}
              monthlyData={monthlyData}
              calculateYearOverYearChange={calculateYearOverYearChange}
              calculateQuarterlyAverageRente={calculateQuarterlyAverageRente}
              calculateYearOverYearInflation={calculateYearOverYearInflation}
              calculateQuarterlyAverageUnemployment={calculateQuarterlyAverageUnemployment}
            />
          } 
        />
        <Route path="/explanations" element={<Explanations />} />
      </Routes>
    </div>
  );
}

export default App;